import { Sort } from '../custom-sort/custom-sort.directive';

export class ClientTableDataSource<T> {
  filterPredicate: (data: T, filter: string) => boolean = defaultFilterPredicate;
  sortingDataAccessor: (data: T, property: string) => string | number = defaultSortingDataAccessor;

  filteredData: T[] = [];
  renderedData: T[] = [];

  private _data: T[];
  private _filter = '';
  private _sort: Sort = null;
  private _pageIndex = 0;
  private _pageSize: number = null;

  constructor(data: T[] = []) {
    this._data = data;
    this.update();
  }

  get data(): T[] {
    return this._data;
  }
  set data(data: T[]) {
    this._data = data;
    this.update();
  }

  get filter(): string {
    return this._filter;
  }
  set filter(filter: string) {
    this._filter = filter;
    this.update();
  }

  get sort(): Sort {
    return this._sort;
  }
  set sort(sort: Sort) {
    this._sort = sort;
    this.update();
  }

  get pageIndex(): number {
    return this._pageIndex;
  }

  get pageSize(): number {
    return this._pageSize;
  }

  setPage(pageIndex: number, pageSize: number): void {
    this._pageIndex = pageIndex;
    this._pageSize = pageSize;
    this.update();
  }

  private update(): void {
    this.filteredData = this._filter
      ? this._data.filter(item => this.filterPredicate(item, this._filter))
      : this._data;

    this._pageIndex = this.getClampedPageIndex();
    this.renderedData = this.pageData(this.sortData(this.filteredData));
  }

  private sortData(data: T[]): T[] {
    if (!this._sort?.active || this._sort.direction === '') {
      return data;
    }

    const { active, direction } = this._sort;

    return data.slice().sort((a, b) => compare(
      this.sortingDataAccessor(a, active),
      this.sortingDataAccessor(b, active)
    ) * (direction === 'asc' ? 1 : -1));
  }

  private pageData(data: T[]): T[] {
    if (!this._pageSize) {
      return data;
    }

    const startIndex = this._pageIndex * this._pageSize;

    return data.slice(startIndex, startIndex + this._pageSize);
  }

  private getClampedPageIndex(): number {
    if (this._pageIndex <= 0 || !this._pageSize) {
      return this._pageIndex;
    }

    const lastPageIndex = Math.max(Math.ceil(this.filteredData.length / this._pageSize) - 1, 0);

    return Math.min(this._pageIndex, lastPageIndex);
  }
}

export function defaultFilterPredicate<T>(data: T, filter: string): boolean {
  const dataString = Object.keys(data).reduce((current, key) => current + data[key] + '◬', '').toLowerCase();

  return dataString.includes(filter.trim().toLowerCase());
}

export function defaultSortingDataAccessor<T>(data: T, property: string): string | number {
  const value = data[property];

  if (isNumberValue(value)) {
    const numberValue = Number(value);

    return numberValue < Number.MAX_SAFE_INTEGER ? numberValue : value;
  }

  return value;
}

function isNumberValue(value: unknown): boolean {
  return !isNaN(parseFloat(value as string)) && !isNaN(Number(value));
}

function compare(a: string | number, b: string | number): number {
  const mixedTypes = typeof a !== typeof b;
  const valueA = mixedTypes && typeof a === 'number' ? `${a}` : a;
  const valueB = mixedTypes && typeof b === 'number' ? `${b}` : b;

  if (valueA != null && valueB != null) {
    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
  }

  if (valueA != null) {
    return 1;
  }

  return valueB != null ? -1 : 0;
}
